/**
 * APEX v7.7.3 — Objectif de saison
 *
 * Un seul objectif de saison par user (stocké dans user_settings.season_objective).
 * Projection LP basée sur les 30 derniers jours.
 *
 * Voir docs/PRD_OBJECTIVES_V7.7_FINAL.md section 8.
 */

import { DAY_MS, MONTH_MS } from '../constants';

const TIER_ORDER = [
  'IRON', 'BRONZE', 'SILVER', 'GOLD',
  'PLATINUM', 'EMERALD', 'DIAMOND',
  'MASTER', 'GRANDMASTER', 'CHALLENGER'
];
const RANK_ORDER = ['IV', 'III', 'II', 'I'];

// Fin de saison 2026 par défaut (à ajuster si Riot communique une date précise)
const SEASON_2026_END = new Date('2026-12-31T23:59:59Z');

/**
 * Convertit tier+rank+lp en valeur absolue de LP cumulés (pour comparer 2 rangs).
 * Iron IV 0LP = 0 ; chaque division = 100 LP ; Master+ = LP réels au-dessus.
 */
export function toAbsoluteLp(tier, rank, lp) {
  const tierIdx = TIER_ORDER.indexOf(tier);
  if (tierIdx === -1) return 0;

  // Tiers Apex (Master+) : pas de divisions
  if (tier === 'MASTER' || tier === 'GRANDMASTER' || tier === 'CHALLENGER') {
    // Master = base après Diamond I (7 tiers * 400)
    const masterBase = 7 * 400;
    const apexOffset = (TIER_ORDER.indexOf(tier) - TIER_ORDER.indexOf('MASTER')) * 500;
    return masterBase + apexOffset + (lp || 0);
  }

  const rankIdx = RANK_ORDER.indexOf(rank);
  const divisionLp = (rankIdx >= 0 ? rankIdx : 0) * 100;
  return tierIdx * 400 + divisionLp + (lp || 0);
}

/**
 * Calcule la progression vers l'objectif de saison.
 * @param {Object} seasonObjective - { target_tier, target_rank, target_lp }
 * @param {Object} userSettings - { current_tier, current_rank, current_lp }
 * @param {Array} rankSnapshots - historique des rangs (pour projection)
 * @returns {Object} { percent, currentLabel, targetLabel, projection, gapLp }
 */
export function computeSeasonProgress(seasonObjective, userSettings, rankSnapshots = [], apexTiers = null) {
  if (!seasonObjective || !userSettings) return null;

  const APEX = ['MASTER', 'GRANDMASTER', 'CHALLENGER'];
  const currentTier = userSettings.current_tier;
  const targetTier = seasonObjective.target_tier;

  const currentTierIdx = TIER_ORDER.indexOf(currentTier);
  const targetTierIdx = TIER_ORDER.indexOf(targetTier);

  const currentAbs = toAbsoluteLp(
    currentTier, userSettings.current_rank, userSettings.current_lp
  );
  const startAbs = toAbsoluteLp('IRON', 'IV', 0);
  const targetAbs = toAbsoluteLp(
    targetTier, seasonObjective.target_rank || 'IV', seasonObjective.target_lp || 0
  );

  // Un objectif sur un tier Apex est ATTEINT seulement si on est réellement
  // dans ce tier ou au-dessus (système de slots, pas de seuil LP fixe).
  let achieved;
  if (APEX.includes(targetTier)) {
    achieved = currentTierIdx >= targetTierIdx;
  } else {
    achieved = currentAbs >= targetAbs;
  }

  // v7.8.2 : cas spécial Master → Grandmaster (le plus courant).
  // On utilise le VRAI cutoff (Edge Function apex-tiers) pour un % juste.
  // Master avec 0 LP = 0% ; Master au cutoff = ~99% (pas encore promu) ;
  // promu GM = 100%.
  let percent;
  let cutoffInfo = null;

  const isMasterToGm =
    currentTier === 'MASTER' && targetTier === 'GRANDMASTER' && !achieved;
  const isGmToChall =
    currentTier === 'GRANDMASTER' && targetTier === 'CHALLENGER' && !achieved;

  if (achieved) {
    percent = 100;
  } else if (isMasterToGm && apexTiers?.masterToGmCutoff) {
    const cutoff = apexTiers.masterToGmCutoff;
    const lp = userSettings.current_lp || 0;
    percent = Math.max(0, Math.min(99, Math.round((lp / cutoff) * 100)));
    cutoffInfo = { cutoff, lp, remaining: Math.max(0, cutoff - lp), type: 'master_gm' };
  } else if (isGmToChall && apexTiers?.gmToChallengerCutoff) {
    const cutoff = apexTiers.gmToChallengerCutoff;
    const lp = userSettings.current_lp || 0;
    percent = Math.max(0, Math.min(99, Math.round((lp / cutoff) * 100)));
    cutoffInfo = { cutoff, lp, remaining: Math.max(0, cutoff - lp), type: 'gm_chall' };
  } else {
    // Cas général : progression linéaire Iron → cible
    const ratio = targetAbs > startAbs
      ? (currentAbs - startAbs) / (targetAbs - startAbs)
      : 0;
    percent = Math.max(0, Math.min(achieved ? 100 : 99, Math.round(ratio * 100)));
  }

  const gapLp = achieved ? 0 : Math.max(0, targetAbs - currentAbs);
  const projection = projectSeasonEnd(rankSnapshots, userSettings, currentAbs);

  return {
    percent,
    currentLabel: formatRank(currentTier, userSettings.current_rank, userSettings.current_lp),
    targetLabel: formatRank(targetTier, seasonObjective.target_rank, seasonObjective.target_lp),
    gapLp,
    achieved,
    isApexTarget: APEX.includes(targetTier),
    cutoffInfo,
    projection
  };
}

/**
 * Projette le rang de fin de saison au rythme actuel.
 */
function projectSeasonEnd(rankSnapshots, userSettings, currentAbs) {
  if (!Array.isArray(rankSnapshots) || rankSnapshots.length < 5) {
    return { available: false, reason: 'Pas assez de données (besoin de 5+ snapshots récents)' };
  }

  const now = Date.now();
  const recent = rankSnapshots
    .filter(s => {
      const ts = new Date(s.created_at || s.timestamp).getTime();
      return now - ts <= MONTH_MS;
    })
    .sort((a, b) => new Date(a.created_at || a.timestamp) - new Date(b.created_at || b.timestamp));

  if (recent.length < 5) {
    return { available: false, reason: 'Pas assez de snapshots sur 30 jours' };
  }

  const first = recent[0];
  const last = recent[recent.length - 1];
  const firstAbs = toAbsoluteLp(first.tier, first.rank, first.lp);
  const lastAbs = toAbsoluteLp(last.tier, last.rank, last.lp);

  const daysSpan = Math.max(1,
    (new Date(last.created_at || last.timestamp) - new Date(first.created_at || first.timestamp)) / DAY_MS
  );
  const lpPerDay = (lastAbs - firstAbs) / daysSpan;

  const daysRemaining = Math.max(0, (SEASON_2026_END.getTime() - now) / DAY_MS);
  const projectedAbs = currentAbs + lpPerDay * daysRemaining;

  return {
    available: true,
    lpPerDay: Math.round(lpPerDay),
    daysRemaining: Math.round(daysRemaining),
    projectedLabel: absToRankLabel(projectedAbs),
    trend: lpPerDay > 5 ? 'up' : (lpPerDay < -5 ? 'down' : 'flat')
  };
}

/**
 * Reconvertit une valeur absolue de LP en label de rang lisible.
 */
function absToRankLabel(abs) {
  const masterBase = 7 * 400;
  if (abs >= masterBase) {
    const over = abs - masterBase;
    if (over >= 1000) return `Challenger (~${Math.round(over - 1000)} LP)`;
    if (over >= 500) return `Grandmaster (~${Math.round(over - 500)} LP)`;
    return `Master (~${Math.round(over)} LP)`;
  }
  const tierIdx = Math.floor(abs / 400);
  const within = abs - tierIdx * 400;
  const rankIdx = Math.floor(within / 100);
  const tier = TIER_ORDER[Math.max(0, Math.min(TIER_ORDER.length - 1, tierIdx))];
  const rank = RANK_ORDER[Math.max(0, Math.min(3, rankIdx))];
  return `${capitalize(tier)} ${rank}`;
}

function formatRank(tier, rank, lp) {
  if (!tier) return '—';
  const APEX = ['MASTER', 'GRANDMASTER', 'CHALLENGER'];
  if (APEX.includes(tier)) {
    return `${capitalize(tier)}${typeof lp === 'number' ? ` ${lp} LP` : ''}`;
  }
  return `${capitalize(tier)}${rank ? ' ' + rank : ''}`;
}

function capitalize(s) {
  if (!s) return '';
  return s.charAt(0) + s.slice(1).toLowerCase();
}

export const SEASON_TARGET_TIERS = TIER_ORDER;
export const SEASON_TARGET_RANKS = RANK_ORDER;
